import { useCallback, useEffect, useRef, useState } from "react";
import SiriWave from "siriwave";
import { AudioVisualizationState } from "@/types/video-call";
import { SIRI_WAVE_CONFIG } from "@/constants/video-call";

interface UseSiriWaveProps {
  isAgentSpeaking: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useSiriWave = ({ isAgentSpeaking, containerRef }: UseSiriWaveProps) => {
  const [siriWave, setSiriWave] = useState<SiriWave | null>(null);

  // Initialize SiriWave
  useEffect(() => {
    if (containerRef.current && !siriWave) {
      try {
        const wave = new SiriWave({
          ...SIRI_WAVE_CONFIG,
          container: containerRef.current,
        });
        setSiriWave(wave);
      } catch (error) {
        // Failed to initialize SiriWave
      }
    }
  }, [containerRef, siriWave]);

  // Helper function to safely start SiriWave
  const startSiriWave = useCallback(() => {
    if (!siriWave) return;

    try {
      // Check if SiriWave has an audioContext property and handle it
      const wave = siriWave as any;
      if (wave.audioContext && wave.audioContext.state === "suspended") {
        wave.audioContext
          .resume()
          .then(() => {
            siriWave.start();
          })
            .catch((e: any) => {
            // Try to start without audio context
            try {
              siriWave.start();
            } catch (startError) {
              // SiriWave start failed
            }
          });
      } else {
        siriWave.start();
      }
    } catch (e) {
      // SiriWave start error
    }
  }, [siriWave]);

  // Helper function to safely stop SiriWave
  const stopSiriWave = useCallback(() => {
    if (!siriWave) return;

    try {
      siriWave.stop();
    } catch (e) {
      // SiriWave stop error
    }
  }, [siriWave]);

  // Set wave amplitude based on audio level
  const setWaveAmplitude = useCallback((level: number) => {
    if (siriWave) {
      try {
        siriWave.setAmplitude(Math.min(level, 4)); // Scale amplitude, max 4
      } catch (e) {
        // SiriWave setAmplitude error
      }
    }
  }, [siriWave]);

  // Control wave based on agent speaking state
  useEffect(() => {
    if (isAgentSpeaking) {
      startSiriWave();
    } else {
      stopSiriWave();
    }
  }, [isAgentSpeaking, startSiriWave, stopSiriWave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (siriWave) {
        try {
          siriWave.stop();
          // Note: SiriWave doesn't have a destroy method, but stopping should be sufficient
        } catch (e) {
          // SiriWave cleanup error
        }
      }
    };
  }, [siriWave]);

  return {
    siriWave,
    startSiriWave,
    stopSiriWave,
    setWaveAmplitude,
  };
};
